import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api';
import { getActiveZoom } from '../../lib/zoomPosition';
import type { SummaryResponse } from '../../types';

interface SummaryPopoverProps {
  targetEl: HTMLElement;
  citeKey: string;
  title?: string;
  onClose: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

export function SummaryPopover({
  targetEl,
  citeKey,
  title,
  onClose,
  onMouseEnter,
  onMouseLeave,
}: SummaryPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [positioned, setPositioned] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; flipped: boolean }>({
    top: 0,
    left: 0,
    flipped: false,
  });

  const recalcPosition = useCallback(() => {
    const popoverEl = popoverRef.current;
    if (!popoverEl) return;

    // Work in unscaled CSS px throughout; see getActiveZoom.
    const z = getActiveZoom();
    const rect = targetEl.getBoundingClientRect();
    const popoverRect = popoverEl.getBoundingClientRect();
    const popoverWidth = (popoverRect.width / z) || 360;
    const popoverHeight = (popoverRect.height / z) || 260;
    let top = rect.bottom / z + 6;
    let left = rect.left / z;
    let flipped = false;

    // Flip above if near bottom
    if (top + popoverHeight > window.innerHeight / z - 20) {
      top = rect.top / z - popoverHeight - 6;
      flipped = true;
    }
    // Align right if near right edge
    if (left + popoverWidth > window.innerWidth / z - 20) {
      left = window.innerWidth / z - popoverWidth - 20;
    }
    if (left < 10) left = 10;

    setPos({ top, left, flipped });
    setPositioned(true);
  }, [targetEl]);

  // Initial position calculation
  useLayoutEffect(() => {
    recalcPosition();
  }, [recalcPosition]);

  // Recalculate when content changes (e.g. loading spinner → actual summary)
  useEffect(() => {
    const popoverEl = popoverRef.current;
    if (!popoverEl) return;
    const observer = new ResizeObserver(() => recalcPosition());
    observer.observe(popoverEl);
    return () => observer.disconnect();
  }, [recalcPosition]);

  // Dismiss on Escape or target disconnected
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const checkTarget = setInterval(() => {
      if (!targetEl.isConnected) onClose();
    }, 500);

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearInterval(checkTarget);
    };
  }, [targetEl, onClose]);

  // Fetch summary — shares cache key with SourceViewPage
  const { data, isLoading } = useQuery<SummaryResponse>({
    queryKey: ['source-summary', citeKey],
    queryFn: () =>
      apiFetch<SummaryResponse>(
        `/api/fastapi/documents/${encodeURIComponent(citeKey)}/summary`,
      ),
    staleTime: 5 * 60 * 1000,
  });

  const hasTags =
    !!(data?.topic || (data?.actors?.length ?? 0) > 0 || (data?.families?.length ?? 0) > 0);

  const popoverContent = (
    <div
      ref={popoverRef}
      className={`summary-popover${pos.flipped ? ' summary-popover--above' : ''}`}
      style={{ top: pos.top, left: pos.left, visibility: positioned ? 'visible' : 'hidden' }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Header */}
      <div className="summary-popover-header">
        {title && <span className="summary-popover-title">{title}</span>}
        <span className="summary-popover-cite">{citeKey}</span>
      </div>

      {isLoading ? (
        <div className="summary-popover-loading">
          <span className="summary-popover-spinner" />
          <span className="summary-popover-loading-label">Loading summary</span>
        </div>
      ) : !data?.summary ? (
        <div className="summary-popover-empty">No summary available</div>
      ) : (
        <>
          <div className="summary-popover-body">
            <p className="summary-popover-text">{data.summary}</p>
          </div>
          {hasTags && (
            <div className="summary-popover-tags">
              {data.topic && (
                <span className="summary-popover-tag summary-popover-tag--topic">
                  {data.topic}
                </span>
              )}
              {data.families?.map((f) => (
                <span key={f} className="summary-popover-tag summary-popover-tag--entity">
                  {f}
                </span>
              ))}
              {data.actors?.map((a) => (
                <span key={a} className="summary-popover-tag summary-popover-tag--entity">
                  {a}
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );

  return createPortal(popoverContent, document.body);
}
